import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { clubSidebar } from '@/config/sidebarConfig.ts';
import { approveClaimApi, getClubApi, getClubPendingClaimsApi, rejectClaimApi } from '@/lib/api/clubsApi';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { COMP_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
} from '@shared';
import type { Competition } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Building, Calendar, ClipboardCheck, MapPin, Plus, Shield, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';

import { PendingClaimsPanel } from '../../components/admin/PendingClaimsPanel';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

const FRONT_URL = import.meta.env.VITE_URL_FRONT as string;

function CompetitionCard({ c }: { c: Competition }) {
  const cfg = COMP_STATUS_CONFIG[c.status] ?? { label: c.status, bg: 'bg-slate-100', text: 'text-slate-600' };
  const progress = c.maxTeams ? Math.round((c.currentTeams / c.maxTeams) * 100) : 0;
  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-emerald-400 hover:shadow-lg'>
      <div className='mb-3 flex items-start justify-between'>
        <div className='flex-1'>
          <div className='mb-2 flex flex-wrap items-center gap-2'>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
              {cfg.label}
            </span>
            <span className='rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>
              {c.type ? TYPE_LABELS[c.type] : '—'}
            </span>
          </div>
          <h3 className='font-bold text-slate-900'>{c.name}</h3>
          <p className='mt-1 text-sm text-slate-500'>
            {c.date ? formatDateAbbreviatedFr(c.date) : '—'}
            {(c.venue ?? c.city) && ` · ${c.venue ?? c.city}`}
          </p>
        </div>
      </div>
      <div className='mt-3 border-t border-slate-100 pt-3'>
        <div className='flex items-center justify-between text-sm'>
          <span className='text-slate-500'>
            {c.level ? LEVEL_LABELS[c.level] : '—'} · {c.price ? `${(c.price / 100).toFixed(0)}€/équipe` : 'Gratuit'}
          </span>
          <span className='font-semibold text-slate-700'>
            {c.currentTeams}/{c.maxTeams ?? '?'} équipes
          </span>
        </div>
        {c.maxTeams && (
          <div className='mt-2 h-1.5 w-full rounded-full bg-slate-200'>
            <div
              className={`h-1.5 rounded-full ${progress >= 90 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────
export default function ClubDashboard() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId;
  const displayName = user?.firstName ?? user?.username ?? 'Gestionnaire';

  const {
    data: club,
    isLoading: clubLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId!),
    queryFn: () => getClubApi(clubId!),
    enabled: !!clubId && isAuthenticated,
  });

  const { data: competitionsData, isLoading: compsLoading } = useQuery({
    queryKey: queryKeys.competitions.list({ upcoming: true, limit: 6 }),
    queryFn: () => getCompetitionsApi({ upcoming: true, limit: 6 }),
    enabled: isAuthenticated,
  });

  const competitions = competitionsData?.data ?? [];

  // ── Pending admin claims for this club ──
  const queryClient = useQueryClient();
  const { data: pendingClaims, isLoading: claimsLoading } = useQuery({
    queryKey: ['clubs', clubId, 'claims', 'pending'],
    queryFn: () => getClubPendingClaimsApi(clubId!),
    enabled: !!clubId && isAuthenticated && !!user?.isClubAdmin,
  });

  const approveMutation = useMutation({
    mutationFn: (claimId: number) => approveClaimApi(claimId),
    onSuccess: () => {
      toast.success('Demande approuvée');
      void queryClient.invalidateQueries({ queryKey: ['clubs', clubId, 'claims', 'pending'] });
    },
    onError: (err: Error) => toast.error(err.message || "Erreur lors de l'approbation"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ claimId, reason }: { claimId: number; reason?: string }) => rejectClaimApi(claimId, reason),
    onSuccess: () => {
      toast.success('Demande rejetée');
      void queryClient.invalidateQueries({ queryKey: ['clubs', clubId, 'claims', 'pending'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Erreur lors du rejet'),
  });

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-8'>
        <PageHeader
          title={`Bonjour, ${displayName}`}
          subtitle={`Tableau de bord — ${club?.name ?? user?.club?.name ?? 'Votre club'}`}
          actions={
            <Link
              to={ROUTES.CLUB_MEMBERS}
              className='flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700'
            >
              <Plus className='h-5 w-5' />
              Gérer les membres
            </Link>
          }
        />

        {/* Club inactive — pending validation banner */}
        {club && !club.isActive && (
          <div className='mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5'>
            <Shield className='mt-0.5 h-5 w-5 shrink-0 text-amber-500' />
            <div>
              <h3 className='font-bold text-amber-900'>Club en attente de validation</h3>
              <p className='mt-1 text-sm text-amber-700'>
                Votre demande d'administrateur est en cours. Le club sera activé une fois que <strong>3 joueurs</strong>{' '}
                auront validé votre demande depuis la page publique du club. En attendant, les fonctionnalités de
                gestion sont limitées.
              </p>
              <a
                href={`${FRONT_URL}/clubs/${clubId}`}
                target='_blank'
                rel='noopener noreferrer'
                className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 hover:text-amber-900'
              >
                Partager la page du club pour obtenir des votes <ArrowRight className='h-4 w-4' />
              </a>
            </div>
          </div>
        )}

        {/* Pending admin claims */}
        {user?.isClubAdmin && pendingClaims?.[0]?.requesterId !== user.id && (
          <PendingClaimsPanel
            claims={pendingClaims ?? []}
            isLoading={claimsLoading}
            onApprove={claimId => approveMutation.mutate(claimId)}
            onReject={(claimId, reason) => rejectMutation.mutate({ claimId, reason })}
            approvePending={approveMutation.isPending}
            rejectPending={rejectMutation.isPending}
          />
        )}

        {/* Club info cards */}
        <div className='mb-8 grid grid-cols-2 gap-4 md:grid-cols-4'>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
              <Building className='h-5 w-5 text-emerald-600' />
            </div>
            <p className='text-xs text-slate-500'>Statut</p>
            {clubLoading ? (
              <div className='mt-1 h-5 w-20 animate-pulse rounded bg-slate-200' />
            ) : (
              <p className={`mt-1 text-sm font-bold ${club?.isActive ? 'text-emerald-600' : 'text-red-600'}`}>
                {club?.isActive ? 'Club actif' : 'Inactif'}
              </p>
            )}
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
              <MapPin className='h-5 w-5 text-blue-600' />
            </div>
            <p className='text-xs text-slate-500'>Région</p>
            {clubLoading ? (
              <div className='mt-1 h-5 w-24 animate-pulse rounded bg-slate-200' />
            ) : (
              <p className='mt-1 text-sm font-bold text-slate-900'>{club?.region ?? '—'}</p>
            )}
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100'>
              <Users className='h-5 w-5 text-purple-600' />
            </div>
            <p className='text-xs text-slate-500'>Membres</p>
            <Link to={ROUTES.CLUB_MEMBERS} className='mt-1 block text-sm font-bold text-blue-600 hover:underline'>
              Voir la liste →
            </Link>
          </div>
          <div className='rounded-2xl border border-slate-200 bg-white p-5'>
            <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100'>
              <Calendar className='h-5 w-5 text-amber-600' />
            </div>
            <p className='text-xs text-slate-500'>Créé le</p>
            {clubLoading ? (
              <div className='mt-1 h-5 w-20 animate-pulse rounded bg-slate-200' />
            ) : (
              <p className='mt-1 text-sm font-bold text-slate-900'>
                {club ? formatDateAbbreviatedFr(club.createdAt) : '—'}
              </p>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          {/* Concours disponibles */}
          <div className='lg:col-span-2'>
            <div className='mb-6 flex items-center justify-between'>
              <h2 className='text-xl font-bold text-slate-900'>Concours à venir</h2>
              <Link
                to={ROUTES.CLUB_COMPETITIONS}
                className='flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700'
              >
                Voir tout <ArrowRight className='h-4 w-4' />
              </Link>
            </div>

            {compsLoading ? (
              <div className='space-y-4'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className='h-28 animate-pulse rounded-2xl bg-slate-200' />
                ))}
              </div>
            ) : competitions.length === 0 ? (
              <div className='rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500'>
                Aucun concours à venir pour le moment
              </div>
            ) : (
              <div className='space-y-4'>
                {competitions.map(c => (
                  <CompetitionCard key={c.id} c={c} />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className='space-y-6'>
            {/* Quick actions */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <h3 className='mb-4 font-bold text-slate-900'>Actions rapides</h3>
              <div className='space-y-2'>
                <Link
                  to={ROUTES.CLUB_MEMBERS}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100'>
                    <Users className='h-5 w-5 text-emerald-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Gérer les membres</p>
                    <p className='text-xs text-slate-500'>Voir et administrer</p>
                  </div>
                </Link>
                <Link
                  to={ROUTES.CLUB_COMPETITIONS}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100'>
                    <Trophy className='h-5 w-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Concours disponibles</p>
                    <p className='text-xs text-slate-500'>Voir les concours</p>
                  </div>
                </Link>
                <Link
                  to={ROUTES.CLUB_INSCRIPTIONS}
                  className='flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50'
                >
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100'>
                    <ClipboardCheck className='h-5 w-5 text-orange-600' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-slate-900'>Mes inscriptions</p>
                    <p className='text-xs text-slate-500'>Suivre les équipes</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Club profile card */}
            <div className='rounded-2xl border border-slate-200 bg-white p-6'>
              <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
                <Building className='h-5 w-5 text-slate-600' />
                Profil du club
              </h3>
              {clubLoading ? (
                <div className='space-y-3'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='h-4 animate-pulse rounded bg-slate-200' />
                  ))}
                </div>
              ) : (
                <div className='space-y-3'>
                  <div>
                    <p className='text-xs text-slate-500'>Nom</p>
                    <p className='font-semibold text-slate-900'>{club?.name ?? '—'}</p>
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Ville</p>
                    <p className='font-semibold text-slate-900'>
                      {club ? `${club.city || '-'}, ${club.department}` : '—'}
                    </p>
                  </div>
                  {club?.email && (
                    <div>
                      <p className='text-xs text-slate-500'>Email</p>
                      <p className='font-semibold text-slate-900'>{club.email}</p>
                    </div>
                  )}
                  {club?.registrationNumber && (
                    <div>
                      <p className='text-xs text-slate-500'>N° affilié</p>
                      <p className='font-semibold text-slate-900'>{club.registrationNumber}</p>
                    </div>
                  )}
                </div>
              )}
              <Link
                to={ROUTES.CLUB_PROFILE_EDIT}
                className='mt-4 block w-full rounded-xl border border-slate-200 px-4 py-2 text-center text-sm font-semibold text-slate-700 transition hover:border-emerald-300'
              >
                Modifier le profil
              </Link>
              <a
                href={`${FRONT_URL}/clubs/${clubId}`}
                className='mt-4 block w-full rounded-xl px-4 py-2 border bg-emerald-600 text-white text-center font-bold text-sm hover:bg-emerald-700 transition-colors'
              >
                Voir le profil public
              </a>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
